import { createPortal } from "react-dom";
import { useDispatch, useSelector } from "react-redux";
import { useLocation } from "react-router-dom";
import { setNewFilters, emptyFilters } from "../../redux/filtersSlice";
import { useTranslation } from "react-i18next";
import useWindowWidth from "../../hooks/useWindowWidth";
import PillSelect from "../filters/PillSelect";
import FilterSection from "../filters/FilterSection";
import DropdownMultiSelect from "../filters/DropdownMultiSelect";
import alpineLakeIcon from "../../static/images/activities_icons/alpine_lake.png";
import waterfallIcon from "../../static/images/activities_icons/waterfall.png";

const AllFilters = ({ onClose }) => {
    const { t } = useTranslation();
    const dispatch = useDispatch();
    const filters = useSelector(state => state.filters);
    const windowWidth = useWindowWidth();

    // Anteprima del numero di risultati nel CTA (richiesta cliente 2026-07-30).
    // Non serve una chiamata dedicata: ogni tap su un filtro aggiorna `filters` in Redux,
    // il che fa già ripartire la fetch della pagina sottostante e con essa `activitiesAmount`.
    // MapPage usa uno slice diverso da AllActivities, quindi scelgo in base alla rotta.
    const { pathname } = useLocation();
    const isMapPage = pathname === '/map-page';
    const listAmount = useSelector(state => state.userActivities.activitiesAmount);
    const mapAmount = useSelector(state => state.userMapActivities.activitiesAmount);
    const isListLoading = useSelector(state => state.userActivities.isLoading);
    const isMapLoading = useSelector(state => state.userMapActivities.isLoading);
    const resultsCount = isMapPage ? mapAmount : listAmount;
    const isCounting = isMapPage ? isMapLoading : isListLoading;

    const setFilter = (key, value) => dispatch(setNewFilters({ [key]: value }));
    const handleReset = () => dispatch(emptyFilters());

    // --- Opzioni filtri ---

    // Mesi (le chiavi i18n esistenti sono in inglese: january, febrary, march, ...)
    const months = [
        { value: 'Gennaio', label: t('poolBar.AllFilters.january') },
        { value: 'Febbraio', label: t('poolBar.AllFilters.febrary') },
        { value: 'Marzo', label: t('poolBar.AllFilters.march') },
        { value: 'Aprile', label: t('poolBar.AllFilters.april') },
        { value: 'Maggio', label: t('poolBar.AllFilters.may') },
        { value: 'Giugno', label: t('poolBar.AllFilters.june') },
        { value: 'Luglio', label: t('poolBar.AllFilters.july') },
        { value: 'Agosto', label: t('poolBar.AllFilters.august') },
        { value: 'Settembre', label: t('poolBar.AllFilters.september') },
        { value: 'Ottobre', label: t('poolBar.AllFilters.october') },
        { value: 'Novembre', label: t('poolBar.AllFilters.november') },
        { value: 'Dicembre', label: t('poolBar.AllFilters.december') }
    ];

    const activityTypeOptions = [
        { value: 'Acqua', label: t('poolBar.AllFilters.water') },
        { value: 'Aria', label: t('poolBar.AllFilters.air') },
        { value: 'Terra', label: t('poolBar.AllFilters.land') },
        { value: 'Neve', label: t('poolBar.AllFilters.snow') },
        { value: 'Best Views', label: t('poolBar.AllFilters.bestViews') }
    ];

    const difficultyOptions = [
        { value: 'Facile', label: t('poolBar.AllFilters.easy') },
        { value: 'Medio', label: t('poolBar.AllFilters.medium') },
        { value: 'Difficile', label: t('poolBar.AllFilters.hard') }
    ];

    const regionOptions = [
        { value: 'Ascona-Locarno', label: t('poolBar.AllFilters.asconaLocarno') },
        { value: 'Bellinzona e Valli', label: t('poolBar.AllFilters.bellinzonaValli') },
        { value: 'Luganese', label: t('poolBar.AllFilters.luganese') },
        { value: 'Mendrisiotto', label: t('poolBar.AllFilters.mendrisiotto') }
    ];

    const groupOptions = [
        { value: 'Da soli', label: t('poolBar.AllFilters.alone') },
        { value: 'In coppia', label: t('poolBar.AllFilters.couple') },
        { value: 'Fra amici', label: t('poolBar.AllFilters.friends') },
        { value: 'In famiglia', label: t('poolBar.AllFilters.family') },
        { value: 'Per i più piccoli', label: t('poolBar.AllFilters.kids') }
    ];

    // Durata dell'attività: i valori sono bucket in minuti, allineati a DURATION_BUCKETS
    // in ticino_wild_server/src/controllers/activitiesController.js
    const durationOptions = [
        { value: '0-60', label: t('poolBar.AllFilters.durationUpTo1h') },
        { value: '60-120', label: t('poolBar.AllFilters.duration1to2h') },
        { value: '120-300', label: t('poolBar.AllFilters.duration2to5h') },
        { value: '300+', label: t('poolBar.AllFilters.durationOver5h') }
    ];

    // "Altri": ogni opzione ha icona inline
    // NB: 'isForInstagrammers' e 'isMonthAdviced' sono volutamente commentati (richiesta
    // cliente 2026-07-30: nascosti "per il momento", serviranno in futuro). Non rimuovere.
    const otherOptions = [
        { value: 'Dog friendly', label: t('poolBar.AllFilters.dogFriendly'), icon: 'fi fi-rr-paw' },
        { value: 'Laghetti alpini', label: t('poolBar.AllFilters.alpineLakes'), icon: <img src={alpineLakeIcon} alt="" className="w-4 h-4 object-contain" /> },
        { value: 'Accessibile', label: t('poolBar.AllFilters.accessible'), icon: 'fi fi-rr-wheelchair' },
        { value: 'MTB', label: t('poolBar.AllFilters.mountainBike'), icon: 'fi fi-rr-biking-mountain' },
        { value: 'Cascate, fiumi e pozze', label: t('poolBar.AllFilters.waterPlace'), icon: <img src={waterfallIcon} alt="" className="w-4 h-4 object-contain" /> },
        { value: 'Griglia fissa', label: t('poolBar.AllFilters.fixedGrill'), icon: 'fi fi-rr-grill' },
        // { value: 'isForInstagrammers', label: t('poolBar.AllFilters.forInstagrammers'), icon: 'fi fi-rr-camera' },
        // { value: 'isMonthAdviced', label: t('poolBar.AllFilters.monthAdvices'), icon: 'fi fi-rr-star' }
    ];

    const content = (
        <div className={`
            ${windowWidth < 768 ? 'fixed inset-0 overflow-y-auto pt-[calc(max(env(safe-area-inset-top),20px)+0.5rem)]' : windowWidth > 1024 ? 'w-[900px] mt-2 rounded-xl' :  'w-[680px] mt-2 rounded-xl'}
            bg-white z-50 flex flex-col
        `}>
            {/* Header: chiudi · Filtri · Reimposta */}
            <div className="flex items-center justify-between px-4 py-4 sticky top-0 bg-white z-20">
                <button
                    type="button"
                    onClick={onClose}
                    className="text-4xl text-[#225424] leading-none"
                    aria-label={t('poolBar.closeFilters')}
                >
                    <i className="fi fi-rr-circle-xmark"></i>
                </button>
                <h3 className="text-[#225424] text-base font-bold tracking-wide">{t('poolBar.AllFilters.filtersTitle')}</h3>
                <button
                    type="button"
                    onClick={handleReset}
                    className="text-[#225424] text-sm cursor-pointer"
                    title={t('poolBar.AllFilters.reset')}
                >
                    {t('poolBar.AllFilters.reset')}
                </button>
            </div>

            {/* Body */}
            <div className="flex flex-col gap-6 px-4 pb-24 md:pb-8">
                {/* Top row: Periodo + Tipo di gruppo (dropdown) */}
                <div className="flex flex-col md:flex-row gap-4">
                    <div className="flex-1">
                        <FilterSection iconClass="fi fi-rr-calendar" title={t('poolBar.AllFilters.periodTitle')}>
                            <DropdownMultiSelect
                                iconClass="fi fi-rr-calendar"
                                placeholder={t('poolBar.AllFilters.periodPlaceholder')}
                                options={months}
                                selected={filters.availablePeriod || []}
                                onChange={(v) => setFilter('availablePeriod', v)}
                            />
                        </FilterSection>
                    </div>
                    <div className="flex-1">
                        <FilterSection iconClass="fi fi-rr-users-alt" title={t('poolBar.AllFilters.groupModelTitle')}>
                            <DropdownMultiSelect
                                iconClass="fi fi-rr-users-alt"
                                placeholder={t('poolBar.AllFilters.groupPlaceholder')}
                                options={groupOptions}
                                selected={filters.groupModel || []}
                                onChange={(v) => setFilter('groupModel', v)}
                            />
                        </FilterSection>
                    </div>
                </div>

                {/* Tipo attività + Difficoltà sulla stessa riga; Regione sotto full width */}
                <div className="grid grid-cols-2 gap-x-8 gap-y-6 items-start">

                    {/* Tipo di attività */}
                    <FilterSection iconClass="fi fi-rr-mountains" title={t('poolBar.AllFilters.activityTypeTitle')}>
                        <PillSelect
                            options={activityTypeOptions}
                            selected={filters.activityType || []}
                            onChange={(v) => setFilter('activityType', v)}
                        />
                    </FilterSection>

                    {/* Livello di difficoltà */}
                    <FilterSection iconClass="fi fi-rr-chart-histogram" title={t('poolBar.AllFilters.difficultyLevelTitle')}>
                        <PillSelect
                            options={difficultyOptions}
                            selected={filters.difficultyLevel || []}
                            onChange={(v) => setFilter('difficultyLevel', v)}
                        />
                    </FilterSection>

                    {/* Regione: sempre full width */}
                    <div className="col-span-2">
                        <FilterSection iconClass="fi fi-rr-marker" title={t('poolBar.AllFilters.regionTitle')}>
                            <PillSelect
                                options={regionOptions}
                                selected={filters.region || []}
                                onChange={(v) => setFilter('region', v)}
                            />
                        </FilterSection>
                    </div>
                </div>

                {/* Durata dell'attività */}
                <FilterSection iconClass="fi fi-rr-clock" title={t('poolBar.AllFilters.durationTitle')}>
                    <PillSelect
                        options={durationOptions}
                        selected={filters.activityDuration || []}
                        onChange={(v) => setFilter('activityDuration', v)}
                    />
                </FilterSection>

                {/* Altri */}
                <FilterSection iconClass="fi fi-rr-paw" title={t('poolBar.AllFilters.othersTitle')}>
                    <PillSelect
                        options={otherOptions}
                        selected={filters.other || []}
                        onChange={(v) => setFilter('other', v)}
                    />
                </FilterSection>
            </div>

            {/* Bottom CTA: Mostra risultati */}
            <div className={`
                ${windowWidth < 768 ? 'fixed bottom-0 left-0 right-0' : 'md:relative md:mt-4 md:mb-4 md:px-4'}
                z-20 px-4 pt-4 pb-[max(env(safe-area-inset-bottom),1rem)] ${windowWidth < 768 ? 'border-t border-neutral-100' : ''}
            `}>
                <button
                    type="button"
                    onClick={onClose}
                    className="w-full flex items-center justify-center gap-2 bg-[#225424] text-white py-3 rounded-full shadow-md md:hover:opacity-90"
                >
                    <i className="fi fi-rr-search leading-none mt-1"></i>
                    <span className="font-medium">{t('poolBar.AllFilters.showResults')}</span>
                    {
                        // Durante il refetch tengo l'ultimo valore noto: evita il lampeggio del contatore
                        typeof resultsCount === 'number' &&
                        <span className={`font-medium ${isCounting ? 'opacity-60' : ''}`}>({resultsCount})</span>
                    }
                </button>
            </div>
        </div>
    );

    // Su mobile renderizzo via Portal in document.body per uscire dallo stacking context di Pool
    // (Pool su mobile è sticky + z-index → crea stacking context che intrappola la modale).
    // Su desktop la modale è un pannello inline sotto i filtri, va lasciata nel flusso normale.
    if (windowWidth < 768 && typeof document !== 'undefined') {
        return createPortal(content, document.body);
    }
    return content;
};

export default AllFilters;
